import {
  CoverageCalculator,
  CoverageCalculatorOptions,
  CustomerInfo,
  ItemInfo,
} from ".";
import {
  getFinalSaleReturnsPrice,
  getPricingRulesInLocalCurrency,
  listsHaveMatchingElement,
} from "./calculator-utils";
import { itemValidForFinalSaleReturns } from "./check-product";

const isProductEnabled = (team: CoverageCalculatorOptions) => {
  return !!team.settings.returns?.finalSaleReturns?.enabled;
};

const getShopperEligibilityInfo = (
  customerInfo: CustomerInfo,
  team: CoverageCalculatorOptions,
) => {
  if (
    listsHaveMatchingElement(
      customerInfo.customerTags,
      team.settings.coverageExcludeCustomerTags,
    )
  ) {
    return { isEligible: false, reasonIneligible: "customer_tags" };
  }

  const finalSaleReturnsEligibleCountries =
    getAllFinalSaleReturnsEligibleCountries(team);

  const ValidCountry =
    finalSaleReturnsEligibleCountries.includes(customerInfo.country) ||
    finalSaleReturnsEligibleCountries.length === 0;

  if (!ValidCountry) {
    return {
      isEligible: false,
      reasonIneligible: "shopper is in an ineligible country",
    };
  }

  if (
    customerInfo.isBusinessAccount &&
    team.settings.shopifyExtension?.disableBusinessAccountInStorefront
  ) {
    return { isEligible: false, reasonIneligible: "business account excluded" };
  }

  if (customerInfo.usingZapietPickup) {
    return { isEligible: false, reasonIneligible: "zapiet pickup excluded" };
  }

  return { isEligible: true, reasonIneligible: undefined };
};

const getItemEligibilityInfo = (
  item: ItemInfo,
  team: CoverageCalculatorOptions,
) => {
  if (item.vendor === "re:do") {
    return { isEligible: false, reasonIneligible: "redo product excluded" };
  }

  if (item.requiresShipping === false) {
    return {
      isEligible: false,
      reasonIneligible: "item does not require shipping",
    };
  }

  const isItemValid = itemValidForFinalSaleReturns({
    itemInfo: { productTags: item.productTags, collections: item.collections },
    validCollections:
      team.settings.returns?.finalSaleReturns?.validCollectionTitles ?? [],
    validProductTags:
      team.settings.returns?.finalSaleReturns?.validProductTags ?? [],
    allEligible:
      team.settings.returns?.finalSaleReturns?.allEligible ?? undefined,
  });

  if (!isItemValid) {
    return {
      isEligible: false,
      reasonIneligible: "item not valid for final sale returns",
    };
  }

  return { isEligible: true, reasonIneligible: undefined };
};

// returns a list of countries. Empty list means all countries
function getAllFinalSaleReturnsEligibleCountries(
  team: CoverageCalculatorOptions,
) {
  if (team.settings.returns?.finalSaleReturns?.pricingRuleSet?.length) {
    // The second set of pricing is international. so all countries are eligible.
    if (
      team.settings.returns?.finalSaleReturns?.pricingRuleSet[1] &&
      !team.settings.returns?.finalSaleReturns?.pricingRuleSet[1].countries
        .length
    ) {
      return [];
    } else {
      return team.settings.returns?.finalSaleReturns?.pricingRuleSet[0]
        .countries;
    }
  }
  return [];
}

const calculateCoveragePrice = (
  team: CoverageCalculatorOptions,
  country: string | undefined,
  numProducts: number,
  amount: number,
  exchangeRate?: number,
  hasSubscriptionItem?: boolean,
): number => {
  if (exchangeRate && team.settings.returns?.finalSaleReturns) {
    team.settings.returns.finalSaleReturns.minPrice *= exchangeRate;
    team.settings.returns.finalSaleReturns.maxPrice *= exchangeRate;

    if (team.settings.returns.finalSaleReturns.shippingLabelCoveragePrice) {
      team.settings.returns.finalSaleReturns.shippingLabelCoveragePrice *=
        exchangeRate;
    }

    if (team.settings.returns.finalSaleReturns.pricingRuleSet) {
      team.settings.returns.finalSaleReturns.pricingRuleSet =
        getPricingRulesInLocalCurrency(
          team.settings.returns.finalSaleReturns.pricingRuleSet,
          exchangeRate,
        );
    }
  }

  let coveragePrice = 0;
  if (team.settings.returns?.finalSaleReturns) {
    coveragePrice = getFinalSaleReturnsPrice({
      cartTotal: amount,
      pricingRuleSets: team.settings.returns.finalSaleReturns.pricingRuleSet,
      shippingLabelCoveragePrice:
        team.settings.returns.finalSaleReturns.shippingLabelCoveragePrice,
      country,
      minPrice: team.settings.returns.finalSaleReturns.minPrice,
      maxPrice: team.settings.returns.finalSaleReturns.maxPrice,
      backupPercentage: team.settings.returns.finalSaleReturns.percentage,
    });
  }

  return coveragePrice;
};

// returns a price if coverage is valid. otherwise returns undefined
const getCoverage = (
  team: CoverageCalculatorOptions,
  customerInfo: CustomerInfo,
  items: ItemInfo[],
  conversionRate?: number,
  ignoreMerchantPaidForEnablementCheck?: boolean,
): { eligible: boolean; price?: number; reasonIneligible?: string } => {
  if (!isProductEnabled(team)) {
    return {
      eligible: false,
      price: undefined,
      reasonIneligible: "product not enabled",
    };
  }

  const shopperEligibleInfo = getShopperEligibilityInfo(customerInfo, team);
  if (!shopperEligibleInfo.isEligible) {
    return {
      eligible: false,
      price: undefined,
      reasonIneligible: shopperEligibleInfo.reasonIneligible,
    };
  }

  let totalPrice = 0;
  let totalValidPrice = 0;
  let numValidItems = 0;
  let numInvalidItems = 0;

  const ineligibleItemReasons = [];

  for (const item of items) {
    const itemPrice = item.price * item.quantity;
    totalPrice += itemPrice;
    const itemEligibleInfo = getItemEligibilityInfo(item, team);
    if (itemEligibleInfo.isEligible) {
      totalValidPrice += itemPrice;
      numValidItems += item.quantity;
    } else {
      numInvalidItems += item.quantity;
      ineligibleItemReasons.push(itemEligibleInfo.reasonIneligible);
    }
  }

  const cartEligibleInfo = getCartEligibilityInfo(
    numValidItems,
    numInvalidItems,
    totalPrice,
    team,
  );
  if (!cartEligibleInfo.isEligible) {
    return {
      eligible: false,
      price: undefined,
      reasonIneligible:
        cartEligibleInfo.reasonIneligible || ineligibleItemReasons.join(", "),
    };
  }

  const price = calculateCoveragePrice(
    team,
    customerInfo.country,
    numValidItems,
    totalValidPrice,
    conversionRate,
    items.some((item) => item.hasSubscription),
  );

  return { eligible: true, price, reasonIneligible: undefined };
};

const getCartEligibilityInfo = (
  numValidItems: number,
  numInvalidItems: number,
  cartTotal: number,
  team: CoverageCalculatorOptions,
) => {
  return { isEligible: numValidItems > 0, reasonIneligible: undefined };
};

export const finalSaleReturnsCoverageCalculator: CoverageCalculator = {
  type: "Final Sale",
  isProductEnabled,
  getShopperEligibilityInfo,
  getItemEligibilityInfo,
  getCartEligibilityInfo,
  // need to handle non usd here.
  calculateCoveragePrice,
  getCoverage,
};
